import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orgAPI } from '../api/client';
import Breadcrumb from '../components/Breadcrumb';
import { School, MapPin, Plus, X, ArrowRight, Users } from 'lucide-react';

const Organizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await orgAPI.list();
      setOrganizations(response.data);
    } catch (error) {
      console.error('Okullar yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgClick = (orgId) => {
    // Navigate to organization dashboard/planning
    navigate(`/org/${orgId}/planning`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb items={[{ label: 'Okullar', path: null }]} showBackButton={false} />
      
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Okullar</h1>
          <p className="text-gray-600">İlkadım360 akredite okullar</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={20} />
          Yeni Okul Ekle
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {organizations.map((org) => (
          <div 
            key={org.id} 
            onClick={() => handleOrgClick(org.id)}
            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <School className="text-blue-600" size={24} />
              </div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  org.isActive
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {org.isActive ? 'Aktif' : 'Pasif'}
              </span>
            </div>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{org.name}</h3>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin size={16} />
                <span className="text-sm">{org.city}{org.district ? `, ${org.district}` : ''}</span>
              </div>
              {org.contactEmail && (
                <p className="text-sm text-gray-600">{org.contactEmail}</p>
              )}
            </div>

            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Akreditasyon: <span className="font-medium">{org.accreditationLevel || 'Yok'}</span>
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/org/${org.id}/members`);
                    }}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Kullanıcılar"
                    data-testid={`org-members-btn-${org.id}`}
                  >
                    <Users size={18} />
                  </button>
                  <ArrowRight size={18} className="text-blue-600" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <CreateOrgModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            loadOrganizations();
          }}
        />
      )}
    </div>
  );
};

const CreateOrgModal = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    district: '',
    contactEmail: '',
    contactPhone: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await orgAPI.create(formData);
      onSuccess();
    } catch (error) {
      console.error('Okul oluşturulamadı:', error);
      alert('Okul oluşturulamadı: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Yeni Okul Ekle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Okul Adı *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Örn: İlkadım360 Ana Okulu"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Şehir *</label>
              <input
                type="text"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: İstanbul"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">İlçe</label>
              <input
                type="text"
                value={formData.district}
                onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Örn: Kadıköy"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">İletişim Email *</label>
            <input
              type="email"
              required
              value={formData.contactEmail}
              onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="ornek@okul.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">İletişim Telefon</label>
            <input
              type="tel"
              value={formData.contactPhone}
              onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="0555 123 4567"
            />
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Oluşturuluyor...' : 'Okul Oluştur'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Organizations;
